import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  createPVInvoice,
  linkPVInvoiceMovement,
  listPVEligibleMovements,
  listPVInvoices,
  registerPVInvoicePayment,
  unlinkPVInvoiceMovement,
  updatePVInvoice,
} from '../services/projectVitalInvoiceService';
import type { ProjectVitalFinanceAccount } from '../types/projectVital';
import { getProjectVitalFinanceOverview } from '../services/projectVitalService';
import type {
  PVInvoice,
  PVInvoiceAllocation,
  PVInvoiceEligibleMovement,
  PVInvoiceKind,
  PVInvoiceStatus,
  PVInvoiceTax,
  PVInvoiceWrite,
} from '../types/projectVitalInvoices';

const COLOR = '#4DD0E1';
const GREEN = '#4ade80';
const RED = '#fca5a5';

const invoiceKindLabel: Record<PVInvoiceKind, string> = {
  issued: 'Emitida',
  received: 'Recibida',
};
const invoiceStatusLabel: Record<PVInvoiceStatus, string> = {
  draft: 'Borrador',
  recorded: 'Registrada',
  void: 'Anulada',
};
const invoiceTaxLabel: Record<PVInvoiceTax, string> = {
  pending: 'Pendiente de clasificar',
  taxable: 'Sujeta',
  exempt: 'Exenta',
  out_of_scope: 'No sujeta',
};

function euro(value: number | string): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

function localToday(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

function dateAfterDays(value: string, days: number): string {
  const date = new Date(`${value}T12:00:00`);
  date.setDate(date.getDate() + days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function defaultInvoice(kind: PVInvoiceKind): PVInvoiceWrite {
  return {
    document_kind: kind,
    document_status: 'draft',
    document_number: null,
    counterparty_label: '',
    issue_date: localToday(),
    due_date: null,
    description: null,
    tax_treatment: 'pending',
    base_amount: '',
    tax_amount: '0',
    withholding_amount: '0',
  };
}

/** Convierte cantidades con coma española a decimal exacto transmitido como texto. */
function decimalAmount(text: string): string | null {
  const normalized = text.trim().replace(',', '.');
  if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) return null;
  const value = Number(normalized);
  if (!Number.isFinite(value) || value > 999999999999.99) return null;
  return value.toFixed(2);
}

function message(error: unknown, fallback: string): string {
  const err = error as { response?: { data?: { detail?: unknown } } };
  return typeof err?.response?.data?.detail === 'string'
    ? err.response.data.detail : fallback;
}

type Props = { professionalAccounts: ProjectVitalFinanceAccount[] };

/**
 * Registro interno de facturas + caja profesional.
 * No emite PDF fiscal ni presupone IVA, retenciones o deducibilidad.
 * Las facturas nunca alteran la liquidez hasta registrar un cobro/pago real.
 */
export default function ProjectVitalProfessionalInvoicesSection({
  professionalAccounts,
}: Props) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [kind, setKind] = useState<PVInvoiceKind>('issued');
  const [invoiceFilter, setInvoiceFilter] = useState<'all' | PVInvoiceStatus>('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PVInvoice | null>(null);
  const [form, setForm] = useState<PVInvoiceWrite>(defaultInvoice('issued'));
  const [formError, setFormError] = useState('');
  const [selected, setSelected] = useState<PVInvoice | null>(null);
  const [paymentMode, setPaymentMode] = useState<'new' | 'existing'>('new');
  const [accountId, setAccountId] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(localToday());
  const [paymentDescription, setPaymentDescription] = useState('');
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [existingMovementId, setExistingMovementId] = useState('');
  const [paymentError, setPaymentError] = useState('');

  const invoicesQuery = useQuery({
    queryKey: ['project-vital', 'invoices'],
    queryFn: () => listPVInvoices(),
  });
  const eligibleQuery = useQuery({
    queryKey: ['project-vital', 'invoice-eligible', selected?.id],
    queryFn: () => listPVEligibleMovements(selected!.id),
    enabled: Boolean(selected && paymentMode === 'existing'),
  });
  const globalAccountsQuery = useQuery({
    queryKey: ['project-vital', 'finance', localToday().slice(0, 7), 'global'],
    queryFn: () => getProjectVitalFinanceOverview(localToday().slice(0, 7), 'global'),
    enabled: Boolean(selected && paymentMode === 'new'),
  });
  // El ámbito profesional de la factura es independiente de la cuenta desde
  // la que se paga: un gasto profesional puede abonarse desde una cuenta Personal.
  const availableAccounts = globalAccountsQuery.data
    ? globalAccountsQuery.data.accounts.filter((account) => account.is_active)
    : professionalAccounts;
  const invoices = invoicesQuery.data ?? [];
  const visible = useMemo(
    () => invoices.filter((invoice) => invoice.document_kind === kind
      && (invoiceFilter === 'all' || invoice.document_status === invoiceFilter)),
    [invoices, kind, invoiceFilter],
  );
  const recorded = useMemo(
    () => invoices.filter((invoice) => invoice.document_status === 'recorded'),
    [invoices],
  );
  const issued = recorded.filter((invoice) => invoice.document_kind === 'issued');
  const received = recorded.filter((invoice) => invoice.document_kind === 'received');
  const sumBy = (rows: PVInvoice[], field: 'total_amount' | 'allocated_amount' | 'outstanding_amount') =>
    rows.reduce((total, row) => total + Number(row[field]), 0);
  const issuedPending = sumBy(issued, 'outstanding_amount');
  const receivedPending = sumBy(received, 'outstanding_amount');

  const today = localToday();
  const next30 = dateAfterDays(today, 30);
  const dueInvoices = recorded.filter((invoice) => Number(invoice.outstanding_amount) > 0);
  const overdueInvoices = dueInvoices.filter((invoice) => invoice.due_date && invoice.due_date < today);
  const due30 = dueInvoices.filter((invoice) =>
    invoice.due_date && invoice.due_date >= today && invoice.due_date <= next30,
  );
  const withoutDueDate = dueInvoices.filter((invoice) => !invoice.due_date);
  const duePreview = [...dueInvoices]
    .sort((a, b) => (a.due_date ?? '9999-12-31').localeCompare(b.due_date ?? '9999-12-31'))
    .slice(0, 8);

  async function refreshInvoiceData(updated?: PVInvoice) {
    if (updated) setSelected(updated);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['project-vital', 'invoices'] }),
      queryClient.invalidateQueries({ queryKey: ['project-vital', 'invoice-eligible'] }),
      queryClient.invalidateQueries({ queryKey: ['project-vital', 'finance'] }),
      queryClient.invalidateQueries({ queryKey: ['project-vital', 'finance-history'] }),
      queryClient.invalidateQueries({ queryKey: ['project-vital', 'finance-forecast'] }),
      queryClient.invalidateQueries({ queryKey: ['project-vital', 'cashflow'] }),
    ]);
  }

  const saveMutation = useMutation({
    mutationFn: ({ payload, invoiceId }: { payload: PVInvoiceWrite; invoiceId?: string }) =>
      invoiceId ? updatePVInvoice(invoiceId, payload) : createPVInvoice(payload),
    onSuccess: async () => {
      setFormOpen(false);
      setEditing(null);
      setFormError('');
      await refreshInvoiceData();
      enqueueSnackbar('Factura guardada.', { variant: 'success' });
    },
    onError: (error) => setFormError(message(error, 'No se pudo guardar la factura.')),
  });
  const paymentMutation = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error('Selecciona una factura.');
      const amount = decimalAmount(paymentAmount);
      if (!amount || Number(amount) <= 0) throw new Error('Introduce un importe válido.');
      if (Number(amount) > Number(selected.outstanding_amount)) {
        throw new Error('El importe supera el pendiente de la factura.');
      }
      if (paymentMode === 'existing') {
        if (!existingMovementId) throw new Error('Selecciona un movimiento.');
        return linkPVInvoiceMovement(selected.id, existingMovementId, amount);
      }
      if (!accountId || !paymentDate) throw new Error('Selecciona cuenta y fecha.');
      return registerPVInvoicePayment(selected.id, {
        account_id: accountId,
        amount,
        payment_date: paymentDate,
        description: paymentDescription.trim() || null,
        request_id: requestId,
      });
    },
    onSuccess: async (updated) => {
      await refreshInvoiceData(updated);
      setSelected(null);
      setPaymentError('');
      setRequestId(crypto.randomUUID());
      setPaymentAmount('');
      setExistingMovementId('');
      enqueueSnackbar('Cobro o pago registrado.', { variant: 'success' });
    },
    onError: (error) => setPaymentError(
      error instanceof Error && !('response' in error)
        ? error.message : message(error, 'No se pudo registrar el cobro o pago.'),
    ),
  });
  const unlinkMutation = useMutation({
    mutationFn: ({ invoice, allocation }: { invoice: PVInvoice; allocation: PVInvoiceAllocation }) =>
      unlinkPVInvoiceMovement(invoice.id, allocation),
    onSuccess: async (updated) => {
      await refreshInvoiceData(updated);
      enqueueSnackbar('Vinculación retirada. El movimiento bancario permanece.', {
        variant: 'success',
      });
    },
    onError: (error) => setPaymentError(message(error, 'No se pudo desvincular el movimiento.')),
  });

  function openNew(kindToCreate: PVInvoiceKind) {
    setEditing(null);
    setForm(defaultInvoice(kindToCreate));
    setFormError('');
    setFormOpen(true);
  }
  function openEdit(invoice: PVInvoice) {
    setEditing(invoice);
    setForm({
      document_kind: invoice.document_kind,
      document_status: invoice.document_status,
      document_number: invoice.document_number,
      counterparty_label: invoice.counterparty_label,
      issue_date: invoice.issue_date,
      due_date: invoice.due_date,
      description: invoice.description,
      tax_treatment: invoice.tax_treatment,
      base_amount: invoice.base_amount,
      tax_amount: invoice.tax_amount,
      withholding_amount: invoice.withholding_amount,
    });
    setFormError('');
    setFormOpen(true);
  }
  function saveForm() {
    const base = decimalAmount(form.base_amount);
    const tax = decimalAmount(form.tax_amount);
    const withholding = decimalAmount(form.withholding_amount);
    if (!form.counterparty_label.trim() || base === null || tax === null
        || withholding === null) {
      setFormError('Completa la contraparte y los tres importes correctamente.');
      return;
    }
    if (Number(base) + Number(tax) - Number(withholding) <= 0) {
      setFormError('El total de la factura debe ser positivo.');
      return;
    }
    if (form.document_status === 'recorded' && !form.document_number?.trim()) {
      setFormError('Una factura registrada necesita número.');
      return;
    }
    if (form.due_date && form.due_date < form.issue_date) {
      setFormError('El vencimiento no puede ser anterior a la emisión.');
      return;
    }
    saveMutation.mutate({
      invoiceId: editing?.id,
      payload: {
        ...form,
        counterparty_label: form.counterparty_label.trim(),
        document_number: form.document_number?.trim() || null,
        description: form.description?.trim() || null,
        due_date: form.due_date || null,
        base_amount: base,
        tax_amount: tax,
        withholding_amount: withholding,
      },
    });
  }
  function openPayment(invoice: PVInvoice) {
    setSelected(invoice);
    setPaymentMode('new');
    setAccountId(professionalAccounts[0]?.id ?? '');
    setPaymentAmount(Number(invoice.outstanding_amount).toFixed(2));
    setPaymentDate(localToday());
    setPaymentDescription('');
    setExistingMovementId('');
    setPaymentError('');
    setRequestId(crypto.randomUUID());
  }

  const eligible = eligibleQuery.data ?? [];
  const selectedMovement = eligible.find((m) => m.id === existingMovementId);
  const paymentDisabled = paymentMutation.isPending || unlinkMutation.isPending;

  return (
    <Paper
      elevation={0}
      sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3.5,
        bgcolor: '#111c2f', border: `1px solid ${alpha('#94a3b8', 0.12)}` }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}
        alignItems={{ md: 'center' }} justifyContent="space-between">
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ReceiptLongRoundedIcon sx={{ color: COLOR }} fontSize="small" />
            <Typography sx={{ color: '#f8fafc', fontSize: '1.08rem', fontWeight: 850 }}>
              Facturación profesional
            </Typography>
          </Stack>
          <Typography sx={{ color: '#94a3b8', fontSize: '.81rem', mt: .5 }}>
            Registro interno de facturas, cobros y pagos; no emite documentos fiscales.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => openNew('issued')}
            sx={{ color: '#091624', bgcolor: COLOR, '&:hover': { bgcolor: '#88FFFF' } }}>
            Emitida
          </Button>
          <Button size="small" variant="outlined" startIcon={<AddRoundedIcon />}
            onClick={() => openNew('received')}
            sx={{ borderColor: '#435469', color: '#e2e8f0' }}>
            Recibida
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1, my: 2 }}>
        {[
          { label: 'Facturado', value: sumBy(issued, 'total_amount'), color: GREEN },
          { label: 'Cobrado', value: sumBy(issued, 'allocated_amount'), color: GREEN },
          { label: 'Por cobrar', value: issuedPending, color: '#fcd34d' },
          { label: 'Facturas recibidas', value: sumBy(received, 'total_amount'), color: RED },
          { label: 'Pagado', value: sumBy(received, 'allocated_amount'), color: RED },
          { label: 'Por pagar', value: receivedPending, color: '#fcd34d' },
        ].map((item) => (
          <Box key={item.label} sx={{ minWidth: 0, p: 1.35, borderRadius: 2,
            border: `1px solid ${alpha('#94a3b8', .14)}`, bgcolor: '#172338' }}>
            <Typography sx={{ fontSize: '.73rem', color: '#94a3b8' }}>{item.label}</Typography>
            <Typography sx={{ color: item.color, fontWeight: 800, fontSize: { xs: '1.14rem', sm: '1.43rem' } }}>
              {euro(item.value)}
            </Typography>
          </Box>
        ))}
      </Box>
      <Typography sx={{ color: '#64748b', fontSize: '.71rem', mb: 1 }}>
        Sumas del listado cargado ({recorded.length} registradas); no incluyen borradores ni anuladas.
      </Typography>
      <Stack direction="row" spacing={1} mb={1.5} flexWrap="wrap" useFlexGap>
        {(['issued', 'received'] as const).map((item) => (
          <Button key={item} size="small" onClick={() => { setKind(item); setInvoiceFilter('all'); }}
            sx={{ color: kind === item ? '#091624' : '#cbd5e1',
              bgcolor: kind === item ? COLOR : '#202d40' }}>
            {item === 'issued' ? 'Emitidas' : 'Recibidas'}
          </Button>
        ))}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select value={invoiceFilter} onChange={(e) => setInvoiceFilter(e.target.value as typeof invoiceFilter)}
            aria-label="Filtrar estado de facturas" sx={{ color: '#cbd5e1', height: 32, fontSize: '.81rem' }}>
            <MenuItem value="all">Todos los estados</MenuItem>
            <MenuItem value="draft">Borradores</MenuItem>
            <MenuItem value="recorded">Registradas</MenuItem>
            <MenuItem value="void">Anuladas</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {invoicesQuery.isPending ? <CircularProgress size={22} /> : null}
      {invoicesQuery.isError ? (
        <Alert severity="error" action={<Button onClick={() => void invoicesQuery.refetch()}>Reintentar</Button>}>
          No se pudieron cargar las facturas. Comprueba que el backend y las migraciones estén actualizados.
        </Alert>
      ) : null}
      {!invoicesQuery.isPending && !invoicesQuery.isError && visible.length === 0 ? (
        <Typography sx={{ color: '#94a3b8', fontSize: '.86rem', py: 2 }}>
          No hay facturas con estos filtros. Puedes registrar una con los botones superiores.
        </Typography>
      ) : null}
      <Stack spacing={1}>
        {visible.map((invoice) => {
          const isIssued = invoice.document_kind === 'issued';
          const remaining = Number(invoice.outstanding_amount);
          return (
            <Box key={invoice.id} sx={{ border: `1px solid ${alpha('#94a3b8', .12)}`,
              bgcolor: '#172338', borderRadius: 2.5, p: { xs: 1.4, sm: 1.7 } }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}
                justifyContent="space-between" alignItems={{ sm: 'center' }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={.7} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                      {invoice.document_number || 'Sin número'}
                    </Typography>
                    <Chip label={invoiceStatusLabel[invoice.document_status]} size="small"
                      sx={{ height: 22, color: '#cbd5e1', bgcolor: '#26374c', fontSize: '.72rem' }} />
                    {invoice.document_status === 'recorded' && (
                      <Chip size="small" label={remaining < .005 ? 'Liquidada' :
                        Number(invoice.allocated_amount) > 0 ? 'Parcial' : 'Pendiente'}
                        sx={{ height: 22, color: remaining < .005 ? GREEN : '#fcd34d',
                          bgcolor: '#26374c', fontSize: '.72rem' }} />
                    )}
                  </Stack>
                  <Typography sx={{ color: '#cbd5e1', fontSize: '.84rem', mt: .3, overflowWrap: 'anywhere' }}>
                    {invoice.counterparty_label}
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: '.74rem' }}>
                    {invoice.issue_date} · {invoiceKindLabel[invoice.document_kind]}
                    {invoice.due_date ? ` · Vence ${invoice.due_date}` : ''}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: { sm: 'right' }, minWidth: 160 }}>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>{euro(invoice.total_amount)}</Typography>
                  {invoice.document_status === 'recorded' && (
                    <Typography sx={{ color: isIssued ? GREEN : RED, fontSize: '.81rem' }}>
                      Pendiente: {euro(invoice.outstanding_amount)}
                    </Typography>
                  )}
                </Box>
                <Stack direction="row" spacing={.7} flexWrap="wrap" useFlexGap>
                  <Button size="small" variant="outlined" onClick={() => openEdit(invoice)}
                    sx={{ borderColor: '#40546c', color: '#e2e8f0' }}>Editar</Button>
                  {invoice.document_status === 'recorded' && remaining > 0 && (
                    <Button size="small" onClick={() => openPayment(invoice)}
                      sx={{ bgcolor: COLOR, color: '#081624', '&:hover': { bgcolor: '#88ffff' } }}>
                      {isIssued ? 'Cobrar' : 'Pagar'}
                    </Button>
                  )}
                  {invoice.allocations.length > 0 && (
                    <Button size="small" onClick={() => openPayment(invoice)}
                      sx={{ color: '#94e0eb' }}>Vínculos ({invoice.allocations.length})</Button>
                  )}
                </Stack>
              </Stack>
            </Box>
          );
        })}
      </Stack>
      {invoices.length >= 200 && (
        <Alert severity="info" sx={{ mt: 1.5 }}>Se muestran las 200 facturas más recientes. Se añadirá paginación.</Alert>
      )}

      <Dialog open={formOpen} maxWidth="sm" fullWidth onClose={() => !saveMutation.isPending && setFormOpen(false)}>
        <DialogTitle>{editing ? 'Editar' : 'Registrar'} factura {form.document_kind === 'issued' ? 'emitida' : 'recibida'}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: .5 }}>
            Este registro no genera una factura legal ni calcula IVA/IRPF automáticamente.
          </Alert>
          <Stack spacing={1.6} pt={.5}>
            <TextField label={form.document_kind === 'issued' ? 'Cliente / clínica' : 'Proveedor'}
              required value={form.counterparty_label}
              onChange={(e) => setForm((f) => ({ ...f, counterparty_label: e.target.value }))}
              helperText="Utiliza datos ficticios en el segundo usuario de prueba." />
            <TextField label="Número de documento" value={form.document_number ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, document_number: e.target.value }))}
              helperText="Obligatorio al marcarla como registrada." />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.4}>
              <TextField type="date" fullWidth label="Emisión" InputLabelProps={{ shrink: true }}
                value={form.issue_date} onChange={(e) => setForm((f) => ({ ...f, issue_date: e.target.value }))} />
              <TextField type="date" fullWidth label="Vencimiento" InputLabelProps={{ shrink: true }}
                value={form.due_date ?? ''} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value || null }))} />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.4}>
              <TextField label="Base (€)" fullWidth required inputProps={{ inputMode: 'decimal' }} value={form.base_amount}
                onChange={(e) => setForm((f) => ({ ...f, base_amount: e.target.value }))} />
              <TextField label="Impuesto (€)" fullWidth inputProps={{ inputMode: 'decimal' }} value={form.tax_amount}
                onChange={(e) => setForm((f) => ({ ...f, tax_amount: e.target.value }))} />
              <TextField label="Retención (€)" fullWidth inputProps={{ inputMode: 'decimal' }} value={form.withholding_amount}
                onChange={(e) => setForm((f) => ({ ...f, withholding_amount: e.target.value }))} />
            </Stack>
            <Typography sx={{ fontWeight: 700 }}>
              Total registrado: {euro((Number((form.base_amount || '0').replace(',', '.')) || 0)
                + (Number((form.tax_amount || '0').replace(',', '.')) || 0)
                - (Number((form.withholding_amount || '0').replace(',', '.')) || 0))}
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Tratamiento fiscal</InputLabel>
              <Select value={form.tax_treatment} label="Tratamiento fiscal"
                onChange={(e) => setForm((f) => ({ ...f, tax_treatment: e.target.value as PVInvoiceTax }))}>
                {(Object.keys(invoiceTaxLabel) as PVInvoiceTax[]).map((value) =>
                  <MenuItem key={value} value={value}>{invoiceTaxLabel[value]}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Estado del registro</InputLabel>
              <Select value={form.document_status} label="Estado del registro"
                onChange={(e) => setForm((f) => ({ ...f, document_status: e.target.value as PVInvoiceStatus }))}>
                {(Object.keys(invoiceStatusLabel) as PVInvoiceStatus[]).map((value) =>
                  <MenuItem key={value} value={value}>{invoiceStatusLabel[value]}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Concepto / notas" multiline minRows={2} value={form.description ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={saveMutation.isPending} onClick={() => setFormOpen(false)}>Cancelar</Button>
          <Button variant="contained" disabled={saveMutation.isPending} onClick={saveForm}>
            {saveMutation.isPending ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(selected)} maxWidth="sm" fullWidth onClose={() => !paymentDisabled && setSelected(null)}>
        <DialogTitle>{selected?.document_kind === 'issued' ? 'Cobrar factura' : 'Pagar factura'}</DialogTitle>
        {selected && (
          <DialogContent>
            <Typography sx={{ fontWeight: 700 }}>{selected.document_number} · {selected.counterparty_label}</Typography>
            <Typography sx={{ color: '#94a3b8', mb: 1.5 }}>
              Total {euro(selected.total_amount)} · Pendiente {euro(selected.outstanding_amount)}
            </Typography>
            {selected.allocations.length > 0 && (
              <Stack spacing={.6} mb={1.5}>
                <Typography sx={{ fontSize: '.84rem', fontWeight: 700 }}>Movimientos vinculados</Typography>
                {selected.allocations.map((allocation) => (
                  <Stack key={allocation.id} direction="row" alignItems="center" justifyContent="space-between">
                    <Typography sx={{ fontSize: '.82rem' }}>{euro(allocation.allocated_amount)} · {allocation.movement_id.slice(0, 8)}…</Typography>
                    <Button size="small" color="warning" disabled={paymentDisabled}
                      onClick={() => unlinkMutation.mutate({ invoice: selected, allocation })}>
                      Desvincular
                    </Button>
                  </Stack>
                ))}
                <Divider />
              </Stack>
            )}
            {Number(selected.outstanding_amount) > 0 && (
              <Stack spacing={1.7}>
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={() => { setPaymentMode('new'); setPaymentError(''); }}
                    variant={paymentMode === 'new' ? 'contained' : 'outlined'}>Registrar nuevo</Button>
                  <Button size="small" onClick={() => { setPaymentMode('existing'); setPaymentError(''); }}
                    variant={paymentMode === 'existing' ? 'contained' : 'outlined'}>Vincular existente</Button>
                </Stack>
                {paymentMode === 'new' ? (
                  <>
                    <Alert severity="info">Se crea un único movimiento real y se vincula en una transacción. No dupliques un cobro ya registrado.</Alert>
                    <FormControl fullWidth>
                      <InputLabel>Cuenta de cobro o pago</InputLabel>
                      <Select label="Cuenta de cobro o pago" value={accountId}
                        onChange={(e) => { setAccountId(e.target.value); setRequestId(crypto.randomUUID()); }}>
                        {availableAccounts.map((account) => (
                          <MenuItem key={account.id} value={account.id}>
                            {account.name} · {account.financial_scope === 'personal' ? 'Personal' : 'Profesional'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {globalAccountsQuery.isPending && <CircularProgress size={20} />}
                    {globalAccountsQuery.isError && (
                      <Alert severity="error">No se pudieron cargar todas las cuentas. Reinténtalo antes de registrar el cobro.</Alert>
                    )}
                    {!globalAccountsQuery.isPending && availableAccounts.length === 0 && (
                      <Alert severity="warning">Crea primero una cuenta activa en Finanzas.</Alert>
                    )}
                    <Typography sx={{ fontSize: '.76rem', color: '#94a3b8' }}>
                      Puedes cobrar o pagar desde una cuenta personal. El movimiento seguirá clasificado como profesional.
                    </Typography>
                    <TextField label="Fecha del cobro / pago" type="date" InputLabelProps={{ shrink: true }}
                      value={paymentDate} onChange={(e) => { setPaymentDate(e.target.value); setRequestId(crypto.randomUUID()); }} />
                    <TextField label="Descripción" value={paymentDescription}
                      onChange={(e) => { setPaymentDescription(e.target.value); setRequestId(crypto.randomUUID()); }} />
                  </>
                ) : (
                  <>
                    <Alert severity="info">Utiliza esta opción cuando el movimiento bancario ya existe en Finanzas.</Alert>
                    {eligibleQuery.isPending ? <CircularProgress size={22} /> : null}
                    {eligibleQuery.isError && <Alert severity="error">No se pudieron cargar los movimientos disponibles.</Alert>}
                    <FormControl fullWidth>
                      <InputLabel>Movimiento disponible</InputLabel>
                      <Select value={existingMovementId} label="Movimiento disponible"
                        onChange={(e) => {
                          const movementId = e.target.value;
                          setExistingMovementId(movementId);
                          const movement = eligible.find((m) => m.id === movementId);
                          if (movement) setPaymentAmount(Math.min(
                            Number(movement.unallocated_amount), Number(selected.outstanding_amount),
                          ).toFixed(2));
                        }}>
                        {eligible.map((movement: PVInvoiceEligibleMovement) => (
                          <MenuItem key={movement.id} value={movement.id}>
                            {movement.movement_date} · {euro(movement.unallocated_amount)} disponible
                            {movement.description ? ` · ${movement.description}` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {selectedMovement && (
                      <Typography sx={{ fontSize: '.78rem', color: '#94a3b8' }}>
                        Disponible en movimiento: {euro(selectedMovement.unallocated_amount)}
                      </Typography>
                    )}
                  </>
                )}
                <TextField label="Importe (€)" required inputProps={{ inputMode: 'decimal' }} value={paymentAmount}
                  onChange={(e) => {
                    setPaymentAmount(e.target.value);
                    if (paymentMode === 'new') setRequestId(crypto.randomUUID());
                  }} />
                {paymentError && <Alert severity="error">{paymentError}</Alert>}
              </Stack>
            )}
          </DialogContent>
        )}
        <DialogActions>
          <Button disabled={paymentDisabled} onClick={() => setSelected(null)}>Cerrar</Button>
          {selected && Number(selected.outstanding_amount) > 0 && (
            <Button variant="contained" disabled={paymentDisabled ||
              (paymentMode === 'new' ? !accountId || globalAccountsQuery.isError || globalAccountsQuery.isPending : !existingMovementId)}
              onClick={() => paymentMutation.mutate()}>
              {paymentMutation.isPending ? 'Registrando...' : paymentMode === 'new' ?
                'Registrar movimiento y vincular' : 'Vincular movimiento'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${alpha('#94a3b8', .16)}` }}>
        <Typography sx={{ color: '#f8fafc', fontWeight: 850, fontSize: '1.08rem' }}>
          Próximos cobros y pagos por facturas
        </Typography>
        <Typography sx={{ color: '#94a3b8', fontSize: '.81rem', mt: .4 }}>
          Vencimientos y cantidades todavía no cobradas o pagadas de las facturas registradas.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1, mt: 1.6 }}>
          {[
            { label: 'Por cobrar próximos 30 días', amount: sumBy(due30.filter((item) => item.document_kind === 'issued'), 'outstanding_amount'), color: GREEN },
            { label: 'Por pagar próximos 30 días', amount: sumBy(due30.filter((item) => item.document_kind === 'received'), 'outstanding_amount'), color: RED },
            { label: 'Facturas vencidas pendientes', amount: overdueInvoices.length, count: true, color: '#fcd34d' },
          ].map((metric) => (
            <Box key={metric.label} sx={{ bgcolor: '#172338', borderRadius: 2, p: 1.3,
              border: `1px solid ${alpha('#94a3b8', .13)}` }}>
              <Typography sx={{ color: '#94a3b8', fontSize: '.74rem' }}>{metric.label}</Typography>
              <Typography sx={{ color: metric.color, fontWeight: 850, fontSize: { xs: '1.14rem', sm: '1.35rem' } }}>
                {metric.count ? metric.amount : euro(metric.amount)}
              </Typography>
            </Box>
          ))}
        </Box>
        {withoutDueDate.length > 0 && (
          <Alert severity="info" sx={{ mt: 1.1 }}>
            Hay {withoutDueDate.length} factura(s) pendiente(s) sin fecha de vencimiento. No se incluyen en los importes previstos a 30 días.
          </Alert>
        )}
        {duePreview.length === 0 ? (
          <Typography sx={{ color: '#94a3b8', fontSize: '.83rem', mt: 1.4 }}>
            No tienes facturas registradas con importes pendientes.
          </Typography>
        ) : (
          <Stack spacing={.7} mt={1.3}>
            {duePreview.map((invoice) => (
              <Stack key={invoice.id} direction={{ xs: 'column', sm: 'row' }} spacing={1}
                justifyContent="space-between" sx={{ bgcolor: '#172338', borderRadius: 2, px: 1.2, py: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 750, fontSize: '.84rem' }}>
                    {invoice.document_number || 'Sin número'} · {invoice.counterparty_label}
                  </Typography>
                  <Typography sx={{ color: invoice.due_date && invoice.due_date < today ? '#fcd34d' : '#94a3b8', fontSize: '.76rem' }}>
                    {invoice.document_kind === 'issued' ? 'Cobro' : 'Pago'} · {invoice.due_date ? `Vence ${invoice.due_date}` : 'Sin vencimiento'}
                  </Typography>
                </Box>
                <Typography sx={{ color: invoice.document_kind === 'issued' ? GREEN : RED, fontWeight: 800 }}>
                  {euro(invoice.outstanding_amount)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
        <Typography sx={{ color: '#8391a8', fontSize: '.73rem', mt: 1.2 }}>
          Esta agenda es informativa: no añade automáticamente facturas a las previsiones recurrentes y evita contar dos veces los cobros o pagos.
          Se calcula sobre las {invoices.length} facturas cargadas.
        </Typography>
      </Box>

    </Paper>
  );
}
